
import React, { useState, useEffect, useRef } from "react";
import { X, Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";

type WaitlistPopupProps = {
  onClose: () => void;
};

// Email validation schema
const emailSchema = z.string().email("Please enter a valid email address");

export const WaitlistPopup = ({ onClose }: WaitlistPopupProps) => {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1);
  const [referralCode, setReferralCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [referrals, setReferrals] = useState(0);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  
  // Reset email error when user types
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError("");
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (step === 1) {
      // Validate email
      try {
        emailSchema.parse(email);
        setIsSubmitting(true);
        
        // Simulate API call
        setTimeout(() => {
          // Generate a random referral code
          const code = `SAVYI${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
          setReferralCode(code);
          
          setStep(2);
          setIsSubmitting(false);
          
          // Save to localStorage for persistence
          localStorage.setItem("savyiWaitlistEmail", email);
          localStorage.setItem("savyiReferralCode", code);
          
          toast({
            title: "You're on the waitlist!",
            description: "Generate your referral code to climb the queue.",
          });
        }, 800);
      } catch (error) {
        if (error instanceof z.ZodError) {
          setEmailError(error.errors[0].message);
        } else {
          setEmailError("Please enter a valid email");
        }
      }
    }
  };
  
  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast({
      title: "Copied to clipboard!",
      description: "Share with friends to move up the waitlist.",
    });
  };

  // Handle keyboard navigation and trap focus within modal
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
    
    // Trap focus within modal
    if (e.key === 'Tab') {
      // Implementation of focus trap
      const focusableElements = 
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const modal = e.currentTarget;
      const focusables = Array.from(
        modal.querySelectorAll(focusableElements)
      ) as HTMLElement[];
      
      const firstElement = focusables[0];
      const lastElement = focusables[focusables.length - 1];
      
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  // When popup opens, focus on the email input (step 1) or close button (step 2)
  useEffect(() => {
    if (step === 1) {
      emailInputRef.current?.focus();
    } else {
      closeButtonRef.current?.focus();
    }
  }, [step]);

  // Check for existing data on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("savyiWaitlistEmail");
    const savedReferralCode = localStorage.getItem("savyiReferralCode");
    const savedReferrals = localStorage.getItem("savyiReferrals");
    
    if (savedEmail) {
      setEmail(savedEmail);
    }
    
    if (savedReferralCode) {
      setReferralCode(savedReferralCode);
      setStep(2);
    }
    
    if (savedReferrals) {
      setReferrals(parseInt(savedReferrals) || 0);
    }
  }, []);
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-title"
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl relative animate-scale-in overflow-hidden">
        <button 
          ref={closeButtonRef}
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-800 z-10"
          aria-label="Close popup"
        >
          <X size={20} />
        </button>
        
        {/* Progress indicator */}
        <div className="w-full h-1 bg-gray-100">
          <div 
            className="h-full bg-gradient-to-r from-[#1211ca] to-[#0072ff] transition-all duration-500"
            style={{ width: step === 1 ? "50%" : "100%" }}
            role="progressbar"
            aria-valuenow={step === 1 ? 50 : 100}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        
        <div className="p-6">
          {step === 1 ? (
            <>
              <div className="mb-4 text-center">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#1211ca]/10 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#1211ca]">
                    <path d="M5.52 19c.64-2.2 1.84-3 3.22-3h6.52c1.38 0 2.58.8 3.22 3" />
                    <circle cx="12" cy="10" r="3" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold mb-1" id="waitlist-title">Join the Savyi Waitlist</h2>
                <p className="text-gray-600 text-sm">Be among the first to experience financial freedom that feels like you.</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    ref={emailInputRef}
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={handleEmailChange}
                    className={`w-full rounded-lg ${emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    disabled={isSubmitting}
                    aria-label="Email address"
                    aria-required="true"
                    aria-invalid={emailError ? "true" : "false"}
                    aria-describedby={emailError ? "email-error" : undefined}
                  />
                  {emailError && (
                    <p className="text-red-500 text-xs mt-1" id="email-error">{emailError}</p>
                  )}
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#1211ca] to-[#0072ff] text-white rounded-lg py-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Joining...
                    </span>
                  ) : "Join Waitlist"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="mb-4 text-center">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#1211ca]/10 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#1211ca]">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold mb-1" id="waitlist-title">You're on the list!</h2>
                <p className="text-gray-600 text-sm">Share your referral code to climb the queue and unlock rewards.</p>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Your referral code:</p>
                  <div className="flex gap-2">
                    <div className="bg-white border border-gray-200 rounded px-4 py-2 flex-1 font-mono font-medium text-center text-[#1211ca]">
                      {referralCode}
                    </div>
                    <Button
                      onClick={copyReferralCode}
                      variant="outline"
                      className="border-[#1211ca] text-[#1211ca]"
                      aria-label="Copy referral code to clipboard"
                    >
                      <Copy size={16} className="mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">Unlock these rewards:</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1211ca]/10 flex items-center justify-center text-[#1211ca]">
                        1
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Invite 3 friends</p>
                        <p className="text-xs text-gray-500">Early access 1 month before public launch</p>
                      </div>
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center" aria-label={referrals >= 3 ? "Completed" : "Not completed"}>
                        {referrals >= 3 && <Check size={12} className="text-green-500" />}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1211ca]/10 flex items-center justify-center text-[#1211ca]">
                        2
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Invite a total of 5 friends</p>
                        <p className="text-xs text-gray-500">3 months premium membership free</p>
                      </div>
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center" aria-label={referrals >= 5 ? "Completed" : "Not completed"}>
                        {referrals >= 5 && <Check size={12} className="text-green-500" />}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1211ca]/10 flex items-center justify-center text-[#1211ca]">
                        3
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Invite a total of 10 friends</p>
                        <p className="text-xs text-gray-500">Lifetime 20% discount + exclusive Savyi merch</p>
                      </div>
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center" aria-label={referrals >= 10 ? "Completed" : "Not completed"}>
                        {referrals >= 10 && <Check size={12} className="text-green-500" />}
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={onClose}
                  className="w-full bg-gradient-to-r from-[#1211ca] to-[#0072ff] text-white rounded-lg py-6"
                >
                  Got it, thanks!
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
